import { useCallback, useEffect, useRef, useState } from "react";
import imageLoadingErr from "../../../../assets/images/imageLoadingErr.jpg";
import styled from "styled-components";

// #region SCOPED CSS
const StyledPage = styled.div`
    .nav-btn {
        position: absolute;
        top: 0px;
        align-items: center;
        justify-content: center;
        display: flex;
        height: 100%;
        width: 40px;
    }

    .nav-btn-backward {
        left: 0px;
        box-shadow: inset 40px 0px 30px -25px #1a222d;
    }
    .nav-btn-forward {
        right: 0px;
        box-shadow: inset -40px 0px 30px -25px #1a222d;
    }

    .carousel-image-container {
        background-image: url(./assets/images/carouselBgImg.jpg);
        background-size: cover;
        width: fit-content;
        position: relative;
    }
    &.image-carousel-container {
        width: 74%;
        height: 100%;
        border-radius: 4px;
        border: 1px solid #3c485a;
        background: var(--backgrounds-lines-n-300, #1a222d);
        position: relative;
    }
    .image-carousel-wrapper {
        display: flex;
        gap: 8px;
        overflow-x: scroll;
        margin: 12px 8px;
    }
    .image-carousel-wrapper::-webkit-scrollbar {
        display: none;
    }
    .image-carousel-wrapper div {
        height: 274px;
        background-color: var(--backgrounds-lines-n-300);
        border-radius: 9px;
    }

    /* Prevent image/text selection while dragging */
    &.is-dragging .image-carousel-wrapper {
        cursor: grabbing;
        user-select: none;
    }
    &.is-dragging .image-carousel-wrapper img {
        pointer-events: none;
    }
`;
// #endregion

export const ProductImageCarousel = ({ images = [], pageEnabled = false }) => {
    const wrapperRef = useRef(null);

    // #region STATES
    const [showBackwardButton, setShowBackwardButton] = useState(false);
    const [showForwardButton, setShowForwardButton] = useState(true);
    const [imageErrors, setImageErrors] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const handleScroll = () => {
            setShowBackwardButton(wrapper.scrollLeft > 0);
            setShowForwardButton(wrapper.scrollLeft < wrapper.scrollWidth - wrapper.clientWidth);
        };

        wrapper.addEventListener("scroll", handleScroll);
        handleScroll(); // Set initial state

        return () => wrapper.removeEventListener("scroll", handleScroll);
    }, [images, pageEnabled]); // Re-evaluate when images change

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        let isDraggingLocal = false;
        let startX = 0;
        let scrollLeftAtStart = 0;

        const handleMouseDown = e => {
            if (!pageEnabled) return;
            // Only respond to primary mouse button
            if (e.button !== 0) return;
            isDraggingLocal = true;
            startX = e.pageX - wrapper.offsetLeft;
            scrollLeftAtStart = wrapper.scrollLeft;
            setIsDragging(true);
            e.preventDefault(); // Prevent image drag and text selection
        };

        const handleMouseMove = e => {
            if (!isDraggingLocal) return;
            e.preventDefault();
            const x = e.pageX - wrapper.offsetLeft;
            const scrollOffset = x - startX;
            wrapper.scrollLeft = scrollLeftAtStart - scrollOffset;
        };

        const handleMouseUp = () => {
            if (!isDraggingLocal) return;
            isDraggingLocal = false;
            setIsDragging(false);
        };

        wrapper.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            wrapper.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [pageEnabled]); // Setup drag-to-scroll
    // #endregion

    // #region METHODS
    const handleImageError = url => {
        setImageErrors(prevErrors => ({
            ...prevErrors,
            [url]: true
        }));
    };

    const ensureHttps = url => {
        if (!url.startsWith("https://")) return `https://${url.replace("http://", "")}`;
        return url;
    };

    const scrollWrapper = useCallback(scrollOffset => {
        const wrapper = wrapperRef.current;
        if (wrapper) {
            wrapper.scrollBy({
                left: scrollOffset,
                behavior: "smooth"
            });
        }
    }, []);

    const onScroll = (forward = true) => {
        if (!pageEnabled) return;
        scrollWrapper(forward ? 300 : -300);
    };
    // #endregion

    // #region RENDER
    return (
        <StyledPage
            className={`image-carousel-container${isDragging ? " is-dragging" : ""}`}
            style={{ position: "relative", zIndex: 100, width: "calc(100% - 774px)" }}>
            <div className="image-carousel-wrapper" ref={wrapperRef}>
                {!pageEnabled || !images || images.length === 0
                    ? Array.from({ length: 5 }).map((_, index) => (
                          <div
                              key={`placeholder-${index}`}
                              style={{
                                  minWidth: "173px",
                                  borderRadius: "6px",
                                  backgroundColor: "var(--backgrounds-lines-n-500)"
                              }}
                          />
                      ))
                    : images.map((imgUrl, index) => {
                          const img = ensureHttps(imgUrl);
                          return (
                              <div className="carousel-image-container" key={index}>
                                  <img
                                      src={imageErrors[img] ? imageLoadingErr : img}
                                      alt="Product"
                                      style={{
                                          borderRadius: "6px",
                                          height: "100%",
                                          minWidth: "173px"
                                      }}
                                      onError={() => handleImageError(img)}
                                  />
                              </div>
                          );
                      })}
            </div>
            {showBackwardButton && (
                <CarouselNavButton direction="backward" pageEnabled={pageEnabled} onScroll={() => onScroll(false)} />
            )}
            {showForwardButton && (
                <CarouselNavButton direction="forward" pageEnabled={pageEnabled} onScroll={() => onScroll(true)} />
            )}
        </StyledPage>
    );
    // #endregion
}; // Image carousel with navigation arrows

// #region SMALL COMPONENTS
const CarouselNavButton = ({ direction, pageEnabled, onScroll }) => {
    const isBackward = direction === "backward";

    return (
        <div
            className={`nav-btn nav-btn-${direction} ${!pageEnabled ? "hover-not-allowed text-inactive" : "cursor-pointer text-normal"}`}
            onClick={e => {
                e.preventDefault();
                if (!pageEnabled) return;
                onScroll();
            }}>
            <i className={`fa-regular ${isBackward ? "fa-chevron-left" : "fa-angle-right"}`}></i>
        </div>
    );
};
// #endregion
